/**
 * Umrah Builder API Service
 * Handles communication with backend APIs for hotels, flights, transport, and bookings
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// API Helper
async function fetchAPI(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  
  const response = await fetch(url, { ...defaultOptions, ...options });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  
  return response.json();
}

// ============ HOTEL SERVICES ============

/**
 * Search hotels in a city
 * @param {Object} params - Search parameters
 * @param {string} params.city - City name (makkah, madinah)
 * @param {string} params.checkIn - Check-in date (YYYY-MM-DD)
 * @param {string} params.checkOut - Check-out date (YYYY-MM-DD)
 * @param {number} params.guests - Number of guests
 * @param {number} params.rooms - Number of rooms
 */
export async function searchHotels({ city, checkIn, checkOut, guests = 2, rooms = 1 }) {
  const params = new URLSearchParams({
    city,
    check_in: checkIn,
    check_out: checkOut,
    guests: guests.toString(),
    rooms: rooms.toString(),
  });
  
  return fetchAPI(`/api/umrah-builder/hotels/search?${params}`);
}

/**
 * Get detailed hotel information
 * @param {string} hotelId - Hotel ID
 * @param {Object} params - Search parameters
 */
export async function getHotelDetails(hotelId, { checkIn, checkOut, guests = 2, rooms = 1 }) {
  const params = new URLSearchParams({
    check_in: checkIn,
    check_out: checkOut,
    guests: guests.toString(),
    rooms: rooms.toString(),
  });
  
  return fetchAPI(`/api/umrah-builder/hotels/${hotelId}/details?${params}`);
}

// ============ FLIGHT SERVICES ============

/**
 * Search flights
 * @param {Object} params - Search parameters
 * @param {string} params.origin - Departure airport IATA code
 * @param {string} params.destination - Arrival airport IATA code
 * @param {string} params.departureDate - Departure date (YYYY-MM-DD)
 * @param {string} params.returnDate - Return date (YYYY-MM-DD) - optional
 * @param {number} params.adults - Number of adults
 * @param {number} params.children - Number of children
 * @param {number} params.infants - Number of infants
 * @param {string} params.cabinClass - Cabin class (Y, C, F)
 */
export async function searchFlights({
  origin,
  destination,
  departureDate,
  returnDate,
  adults = 1,
  children = 0,
  infants = 0,
  cabinClass = 'Y'
}) {
  const body = {
    origin,
    destination,
    departure_date: departureDate,
    adults,
    children,
    infants,
    cabin_class: cabinClass,
  };
  
  if (returnDate) {
    body.return_date = returnDate;
  }
  
  return fetchAPI('/api/umrah-builder/flights/search', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ============ TRANSPORT & VISA SERVICES ============

/**
 * Get available transport options with route-based pricing
 * @param {number} passengers - Number of passengers (for vehicle recommendation)
 */
export async function getTransportOptions(passengers = 1) {
  return fetchAPI(`/api/umrah-builder/transport?passengers=${passengers}`);
}

/**
 * Calculate transport cost breakdown
 * @param {Object} params - Transport parameters
 */
export async function calculateTransportPrice({
  vehicleType,
  passengers = 1,
  includeJeddahMakkah = true,
  includeMakkahMadinah = true,
  includeMadinahAirport = false,
  includeMadinahJeddah = true,
  includeMakkahZiyarat = false,
  includeMadinahZiyarat = false,
}) {
  const params = new URLSearchParams({
    vehicle_type: vehicleType,
    passengers: passengers.toString(),
    include_jeddah_makkah: includeJeddahMakkah.toString(),
    include_makkah_madinah: includeMakkahMadinah.toString(),
    include_madinah_airport: includeMadinahAirport.toString(),
    include_madinah_jeddah: includeMadinahJeddah.toString(),
    include_makkah_ziyarat: includeMakkahZiyarat.toString(),
    include_madinah_ziyarat: includeMadinahZiyarat.toString(),
  });
  
  return fetchAPI(`/api/umrah-builder/transport/calculate?${params}`);
}

/**
 * Get available visa options
 */
export async function getVisaOptions() {
  return fetchAPI('/api/umrah-builder/visa');
}

/**
 * Get transport options with dynamic routing based on itinerary
 * @param {Object} params - Itinerary parameters
 */
export async function getItineraryTransport({
  arrivalAirport = 'JED',
  departureAirport = 'MED',
  passengers = 1,
  visitMakkah = true,
  visitMadinah = true
}) {
  const params = new URLSearchParams({
    arrival_airport: arrivalAirport,
    departure_airport: departureAirport,
    passengers: passengers.toString(),
    visit_makkah: visitMakkah.toString(),
    visit_madinah: visitMadinah.toString()
  });
  return fetchAPI(`/api/umrah-builder/transport/itinerary?${params}`);
}


// ============ ROOM TYPES & OCCUPANCY SERVICES ============

/**
 * Get available room types with occupancy rules
 */
export async function getRoomTypes() {
  return fetchAPI('/api/umrah-builder/rooms/types');
}

/**
 * Validate room selection against occupancy rules
 * @param {string} roomType - Room type ID
 * @param {number} guests - Number of guests
 * @param {string} hotelId - Optional hotel ID for family suite capacity
 */
export async function validateRoomSelection(roomType, guests, hotelId = null) {
  const params = new URLSearchParams({
    room_type: roomType,
    guests: guests.toString(),
  });
  if (hotelId) params.append('hotel_id', hotelId);
  return fetchAPI(`/api/umrah-builder/rooms/validate?${params}`);
}

/**
 * Calculate room allocation suggestions
 * @param {number} adults - Number of adults
 * @param {number} children - Number of children
 * @param {number} infants - Number of infants
 * @param {string} roomType - Preferred room type
 * @param {string} hotelId - Optional hotel ID
 */
export async function calculateRoomAllocation(adults, children, infants = 0, roomType = 'double', hotelId = null) {
  const params = new URLSearchParams({
    adults: adults.toString(),
    children: children.toString(),
    infants: infants.toString(),
    room_type: roomType,
  });
  if (hotelId) params.append('hotel_id', hotelId);
  return fetchAPI(`/api/umrah-builder/rooms/calculate?${params}`);
}


// ============ COMBINED FLIGHTS SERVICE ============

/**
 * Get combined flight options with same-airline and mixed-airline pricing
 * @param {Object} params - Flight search parameters
 */
export async function getCombinedFlights({
  origin,
  destination = 'JED',
  returnFrom = 'MED',
  departureDate,
  returnDate,
  adults = 2,
  children = 0,
  infants = 0,
  cabinClass = 'Y'
}) {
  return fetchAPI('/api/umrah-builder/flights/combined', {
    method: 'POST',
    body: JSON.stringify({
      origin,
      destination,
      return_from: returnFrom,
      departure_date: departureDate,
      return_date: returnDate,
      adults,
      children,
      infants,
      cabin_class: cabinClass
    }),
  });
}


// ============ PRICING & BOOKING SERVICES ============

/**
 * Get predefined packages (Economy, Standard, Premium) with live data
 * @param {Object} params - Search parameters
 */
export async function getPredefinedPackages({
  departureAirport,
  arrivalAirport = 'JED',
  returnFrom = 'MED',
  departureDate,
  returnDate,
  makkahNights = 5,
  madinahNights = 3,
  adults = 2,
  children = 0,
  infants = 0,
  currency = 'GBP'
}) {
  return fetchAPI('/api/umrah-builder/packages/predefined', {
    method: 'POST',
    body: JSON.stringify({
      departure_airport: departureAirport,
      arrival_airport: arrivalAirport,
      return_from: returnFrom,
      departure_date: departureDate,
      return_date: returnDate,
      makkah_nights: makkahNights,
      madinah_nights: madinahNights,
      adults,
      children,
      infants,
      currency
    }),
  });
}

/**
 * Calculate package price
 * @param {Object} params - Package parameters
 */
export async function calculatePackagePrice({
  makkahHotelId,
  makkahNights = 0,
  madinahHotelId,
  madinahNights = 0,
  transportId,
  visaId,
  passengers = 1,
  flightPrice = 0
}) {
  const params = new URLSearchParams();
  
  if (makkahHotelId) params.append('makkah_hotel_id', makkahHotelId);
  if (makkahNights > 0) params.append('makkah_nights', makkahNights.toString());
  if (madinahHotelId) params.append('madinah_hotel_id', madinahHotelId);
  if (madinahNights > 0) params.append('madinah_nights', madinahNights.toString());
  if (transportId) params.append('transport_id', transportId);
  if (visaId) params.append('visa_id', visaId);
  params.append('passengers', passengers.toString());
  params.append('flight_price', flightPrice.toString());
  
  return fetchAPI(`/api/umrah-builder/calculate-price?${params}`, {
    method: 'POST',
  });
}

/**
 * Create a booking
 * @param {Object} bookingData - Booking information
 */
export async function createBooking({
  package: packageData,
  customerName,
  customerEmail,
  customerPhone,
  paymentType = 'deposit',
  specialRequests,
  passengers = []
}) {
  return fetchAPI('/api/umrah-builder/book', {
    method: 'POST',
    body: JSON.stringify({
      package: packageData,
      customer_name: customerName,
      customer_email: customerEmail,
      customer_phone: customerPhone,
      payment_type: paymentType,
      special_requests: specialRequests,
      passengers,
    }),
  });
}

/**
 * Get booking PDF
 * @param {string} bookingId - Booking ID
 */
export async function getBookingPDF(bookingId) {
  return fetchAPI(`/api/umrah-builder/booking/${bookingId}/pdf`);
}

/**
 * Verify package availability and pricing before checkout
 * @param {Object} params - Package verification parameters
 */
export async function verifyPackage({
  packageType,
  makkahHotelId,
  madinahHotelId,
  makkahNights = 5,
  madinahNights = 3,
  departureAirport,
  arrivalAirport = 'JED',
  returnFrom = 'MED',
  departureDate,
  returnDate,
  adults = 2,
  children = 0,
  infants = 0,
  boardType = 'room_only',
  originalPrice,
  currency = 'GBP'
}) {
  return fetchAPI('/api/umrah-builder/packages/verify', {
    method: 'POST',
    body: JSON.stringify({
      package_type: packageType,
      makkah_hotel_id: makkahHotelId,
      madinah_hotel_id: madinahHotelId,
      makkah_nights: makkahNights,
      madinah_nights: madinahNights,
      departure_airport: departureAirport,
      arrival_airport: arrivalAirport,
      return_from: returnFrom,
      departure_date: departureDate,
      return_date: returnDate,
      adults,
      children,
      infants,
      board_type: boardType,
      original_price: originalPrice,
      currency
    }),
  });
}

// ============ HEALTH CHECK ============

/**
 * Check API health status
 */
export async function checkAPIHealth() {
  return fetchAPI('/api/umrah-builder/');
}

export default {
  searchHotels,
  getHotelDetails,
  searchFlights,
  getTransportOptions,
  getVisaOptions,
  getRoomTypes,
  validateRoomSelection,
  calculateRoomAllocation,
  getCombinedFlights,
  getPredefinedPackages,
  calculatePackagePrice,
  createBooking,
  getBookingPDF,
  verifyPackage,
  checkAPIHealth,
};
